import { createContext, useContext, useEffect, useState } from 'react';
import useReactNative from './useReactNative';

type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
	children: React.ReactNode;
	defaultTheme?: Theme;
	storageKey?: string;
};

type ThemeProviderState = {
	theme: Theme;
	setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
	theme: 'system',
	setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
	children,
	defaultTheme = 'system',
	storageKey = 'vite-ui-theme',
	...props
}: ThemeProviderProps) {
	const [theme, setTheme] = useState<Theme>(
		() => (localStorage.getItem(storageKey) as Theme) || defaultTheme
	);

	const { isReactNative, postMessage } = useReactNative();

	useEffect(() => {
		if (!isReactNative) return;
		postMessage({ message: theme });
	}, [theme, postMessage, isReactNative]);

	useEffect(() => {
		const root = window.document.documentElement;
		const metaThemeColor = document.querySelector(
			'meta[name="theme-color"]'
		);

		const updateThemeColor = (theme: Theme) => {
			if (metaThemeColor) {
				if (theme === 'dark') {
					metaThemeColor.setAttribute('content', '#000000');
				} else {
					metaThemeColor.setAttribute('content', '#ffffff');
				}
			}
		};

		root.classList.remove('light', 'dark');

		if (theme === 'system') {
			const systemTheme = window.matchMedia(
				'(prefers-color-scheme: dark)'
			).matches
				? 'dark'
				: 'light';

			root.classList.add(systemTheme);
			updateThemeColor(systemTheme);

			return;
		}

		root.classList.add(theme);
		updateThemeColor(theme);
	}, [theme]);

	const value = {
		theme,
		setTheme: (theme: Theme) => {
			localStorage.setItem(storageKey, theme);
			setTheme(theme);
		},
	};

	return (
		<ThemeProviderContext.Provider {...props} value={value}>
			{children}
		</ThemeProviderContext.Provider>
	);
}

export const useTheme = () => {
	const context = useContext(ThemeProviderContext);

	if (context === undefined)
		throw new Error('useTheme must be used within a ThemeProvider');

	return context;
};
